import React, { useRef } from 'react';

export interface TeacherWorkspaceTabDefinition<TTab extends string> {
  key: TTab;
  label: string;
  badge?: number | null;
}

interface TeacherWorkspaceTabsProps<TTab extends string> {
  activeTab: TTab;
  tabs: Array<TeacherWorkspaceTabDefinition<TTab>>;
  onChange: (tab: TTab) => void;
}

export const TeacherWorkspaceTabs = <TTab extends string>({
  activeTab,
  tabs,
  onChange,
}: TeacherWorkspaceTabsProps<TTab>) => {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const selectAndFocusTab = (index: number) => {
    const tab = tabs[index];
    if (!tab) return;
    onChange(tab.key);
    tabRefs.current[index]?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    selectAndFocusTab(nextIndex);
  };

  return (
    <div
      data-testid="teacher-workspace-tabs"
      className="sticky top-0 z-10 rounded-xl border border-slate-200 bg-white/95 p-2 shadow-sm backdrop-blur"
      role="tablist"
      aria-label="Lesson workspace"
    >
      <div className="flex flex-wrap gap-2">
        {tabs.map((tab, index) => (
          <button
            key={`workspace-tab-${tab.key}`}
            ref={(element) => { tabRefs.current[index] = element; }}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            tabIndex={activeTab === tab.key ? 0 : -1}
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
              activeTab === tab.key
                ? 'border-indigo-300 bg-indigo-50 text-indigo-900'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
            onClick={() => onChange(tab.key)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            <span>{tab.label}</span>
            {typeof tab.badge === 'number' && tab.badge > 0 ? (
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                activeTab === tab.key
                  ? 'bg-indigo-100 text-indigo-800'
                  : 'bg-slate-100 text-slate-700'
              }`}
              >
                {tab.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
};
